import { Box, Stack, CircularProgress } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import { GridFooter } from '@mui/x-data-grid'
import DataGridTable from '../dataGrid/DataGridTable'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

const TimeSheetTable = ({ searchTerm }) => {
	const [timeEntries, setTimeEntries] = useState([])
	const [loading, setLoading] = useState(false)
	const [totalHours, setTotalHours] = useState(0)
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0
	})

	// Debounce search term to avoid too many API calls
	const debouncedSearchTerm = useDebounce(searchTerm, 500)

	const fetchTimeEntries = useCallback(async (page = 1, limit = 10, search = '') => {
		try {
			setLoading(true)
			let url = `${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/time-entry-list?page=${page}&limit=${limit}`
			
			// Add search parameter if provided
			if (search && search.trim()) {
				url += `&search=${encodeURIComponent(search.trim())}`
			}

			const response = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			const result = await response.json()
			
			if (result.success) {
				// Transform API data to match table structure
				const transformedData = result.data.map((item) => {
					// Format hour field to show just time or duration
					let formattedHour = item.hour || '0.0'
					
					// Check if hour is a date string and extract time
					if (typeof formattedHour === 'string' && formattedHour.includes('GMT')) {
						// If it's a full date string, extract just the time part
						const date = new Date(formattedHour)
						if (!isNaN(date.getTime())) {
							formattedHour = dayjs(date).format('HH:mm')
						}
					} else if (dayjs(formattedHour).isValid() && formattedHour.length > 10) {
						// If it's a valid date but not a simple time string
						formattedHour = dayjs(formattedHour).format('HH:mm')
					}
					// If it's already a simple number or time format, keep it as is
					
					return {
						id: item._id,
						date: dayjs(item.date).format('YYYY-MM-DD'),
						Wedding: item.kazooId?.kazooName || 'N/A',
						Task: item.taskDetails || 'N/A',
						Hours: formattedHour,
						rawData: item
					}
				})
				
				setTimeEntries(transformedData)
				setPagination({
					page: result.currentPage || page,
					limit: result.limit || limit,
					total: result.total,
					totalPages: result.totalPages
				})
				setTotalHours(result.totalHours || 0)
			} else {
				toast.error(result.message || 'Failed to fetch time entries')
			}
		} catch (error) {
			console.error('Error fetching time entries:', error)
			toast.error('Failed to fetch time entries')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchTimeEntries(1, 10, debouncedSearchTerm)
	}, [debouncedSearchTerm, fetchTimeEntries])

	// Handle pagination changes
	const handlePageChange = (newPage) => {
		fetchTimeEntries(newPage, pagination.limit, debouncedSearchTerm)
	}

	const handlePageSizeChange = (newLimit) => {
		fetchTimeEntries(1, newLimit, debouncedSearchTerm)
	}

	const columns = [
		{
			field: 'date',
			headerName: 'Date',
			flex: 1,
			minWidth: 120,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'Wedding',
			headerName: 'Wedding',
			flex: 1,
			minWidth: 150,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'Task',
			headerName: 'Task',
			flex: 1,
			minWidth: 400,
			renderCell: (params) => {
				return (
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box component="span" sx={{ fontWeight: 500 }}>
							{params.value}
						</Box>
					</Stack>
				)
			},
		},
		{
			field: 'Hours',
			headerName: 'Hours',
			flex: 1,
			minWidth: 200,
			sortable: false,
			renderCell: (params) => (
				<span className="role-text">{params.value || '0.0'}</span>
			),
		},
	]

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<CircularProgress />
			</Box>
		)
	}

	const TotalHoursFooter = () => (
		<>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					px: 1,
					py: 1.5,
					backgroundColor: '#eef2f6',
					borderTop: '1px solid #e0e0e0',
				}}
			>
				{/* Spacers matching Date, Wedding, Task columns */}
				<Box sx={{ flex: 1, minWidth: 120 }} />
				<Box sx={{ flex: 1, minWidth: 150 }} />
				<Box sx={{ flex: 1, minWidth: 400 }} />
				{/* Aligned with Hours column */}
				<Box sx={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
					<span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
						Total Hours:
					</span>
					<span style={{ fontWeight: 700, fontSize: '14px', color: '#333' }}>
						{totalHours}
					</span>
				</Box>
			</Box>
			<GridFooter />
		</>
	)

	return (
		<div>
			<DataGridTable
				rows={timeEntries}
				columns={columns}
				pagination={pagination}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
				checkboxSelection={false}
				slots={{ footer: TotalHoursFooter }}
			/>
		</div>
	)
}

export default TimeSheetTable
